
import { useNavigate, useLocation } from 'react-router-dom';

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId: string) => {
    // Se não estamos na página inicial, navegar para ela primeiro
    if (location.pathname !== '/') {
      navigate('/');
      // Aguardar um pouco para a página carregar antes de fazer o scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Se já estamos na página inicial, fazer scroll direto
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleHomeClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      scrollToSection('home');
    }
  };

  return (
    <footer className="bg-gray-900 border-t border-gray-800">
      <div className="container mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          {/* Logo & Description */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://static.readdy.ai/image/54e10434a90951f6daafe5abdc38c83e/ad38a3b7858105f0ff7bd2bf7c7319ad.png" 
                alt="Take Lead Logo" 
                className="h-10 w-auto"
              />
            </div>
            <p className="text-gray-400 mb-4 max-w-md">
              Transformamos sua prospecção com as mais avançadas tecnologias de Inteligência Artificial, 
              aumentando significativamente suas oportunidades qualificadas.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.linkedin.com/company/take-lead/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-colors cursor-pointer">
                <i className="ri-linkedin-box-fill"></i>
              </a>
              <a href="https://www.facebook.com/take.lead.2025" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-colors cursor-pointer">
                <i className="ri-facebook-fill"></i>
              </a>
              <a href="https://www.instagram.com/takeleadbpo/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-colors cursor-pointer">
                <i className="ri-instagram-fill"></i>
              </a>
              <a href="#" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-blue-600 transition-colors cursor-pointer">
                <i className="ri-youtube-fill"></i>
              </a>
            </div>
          </div>

          {/* Navigation Icons */}
          <div className="flex justify-center md:justify-end">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-6 max-w-md md:max-w-none">
              <button 
                onClick={() => scrollToSection('home')}
                className="flex flex-col items-center text-gray-400 hover:text-white transition-colors cursor-pointer group"
                title="Início"
              >
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-home-line text-lg"></i>
                </div>
                <span className="text-xs">Início</span>
              </button>

              <button 
                onClick={() => scrollToSection('channels')}
                className="flex flex-col items-center text-gray-400 hover:text-white transition-colors cursor-pointer group"
                title="Canais"
              >
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-broadcast-line text-lg"></i>
                </div>
                <span className="text-xs">Canais</span>
              </button>

              <button 
                onClick={() => scrollToSection('services')}
                className="flex flex-col items-center text-gray-400 hover:text-white transition-colors cursor-pointer group"
                title="Serviços"
              >
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-service-line text-lg"></i>
                </div>
                <span className="text-xs">Serviços</span>
              </button>

              <button 
                onClick={() => scrollToSection('differentials')}
                className="flex flex-col items-center text-gray-400 hover:text-white transition-colors cursor-pointer group"
                title="Diferenciais"
              >
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-star-line text-lg"></i>
                </div>
                <span className="text-xs">Diferenciais</span>
              </button>

              <button 
                onClick={() => scrollToSection('process')}
                className="flex flex-col items-center text-gray-400 hover:text-white transition-colors cursor-pointer group"
                title="Processo"
              >
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-flow-chart text-lg"></i>
                </div>
                <span className="text-xs">Processo</span>
              </button>

              <button 
                onClick={() => scrollToSection('contact')}
                className="flex flex-col items-center text-gray-400 hover:text-white transition-colors cursor-pointer group"
                title="Contato"
              >
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-600 transition-colors">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <span className="text-xs">Contato</span>
              </button>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="col-span-1 md:col-span-2 border-t border-gray-800 mt-6 pt-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2025 Take Lead. Todos os direitos reservados.
              </p>
              <div className="flex flex-wrap items-center justify-center sm:justify-end gap-4 sm:gap-6">
                <button 
                  onClick={() => {
                    navigate('/privacy');
                    window.scrollTo(0, 0);
                  }}
                  className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Política de Privacidade
                </button>
                <a 
                  href="#" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Termos de Uso
                </a>
                <a 
                  href="https://readdy.ai/?origin=logo" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  Website Builder
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>
    </footer>
  );
}
